import { useHealthCheckQuery } from "@/common/api/health-check/use-health-check.query";
import { type PropsWithChildren, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const TOAST_ID = "health-status";

export function HealthGuard({ children }: PropsWithChildren) {
  const { t } = useTranslation(["common"]);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" ? !navigator.onLine : false);

  useEffect(() => {
    const onOff = () => setOffline(true);
    const onOn = () => setOffline(false);
    window.addEventListener("offline", onOff);
    window.addEventListener("online", onOn);
    return () => {
      window.removeEventListener("offline", onOff);
      window.removeEventListener("online", onOn);
    };
  }, []);

  const { data: healthy } = useHealthCheckQuery({
    enabled: !offline,
    retry: 0,
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (offline) {
      toast.error(t("common:NoInternetConnection"), { id: TOAST_ID });
      return;
    }
    if (healthy === false) {
      toast.error(t("common:ServerNotReachable"), { id: TOAST_ID });
      return;
    }
    if (healthy === true) {
      toast.dismiss(TOAST_ID);
    }
  }, [offline, healthy, t]);

  return <>{children}</>;
}
